import type { HTMLAttributes, ReactNode } from 'react'

import { Button } from '@/components/Button'
import { ErrorBlock } from '@/components/ErrorBlock'
import { Icon } from '@/components/Icon'

import { Form, FormRow } from '@/components/Form'
import styles from './PasswordReset.module.css'

export interface PasswordResetProps {
  onSubmit: () => unknown
  containsLowerCase: boolean
  containsUpperCase: boolean
  containsOneNumber: boolean
  containsSpecialCharacters: boolean
  validMinimumLength: boolean
  validMaximumLength: boolean
  isValid: boolean
  isResetPending: boolean
  isResetRejected: boolean
  resetError?: unknown
  children: ReactNode
}

export function PasswordReset({
  onSubmit,
  containsLowerCase,
  containsUpperCase,
  containsOneNumber,
  containsSpecialCharacters,
  validMinimumLength,
  validMaximumLength,
  isValid,
  isResetPending,
  isResetRejected,
  resetError,
  children,
}: PasswordResetProps) {
  return (
    <div className={styles.root}>
      <div />
      <div>
        <div className={styles.title}>You have been invited!</div>
        <div className={styles.subtitle}>
          Please set a secure password for signing in and accessing your data.
        </div>
        <div>
          <Form onSubmit={onSubmit}>
            {children}

            <FormRow>
              <div className={styles.requirements}>
                <List>
                  <ListItem
                    title="One lowercase character"
                    valid={containsLowerCase}
                  />
                  <ListItem
                    title="One uppercase character"
                    valid={containsUpperCase}
                  />
                  <ListItem title="One number" valid={containsOneNumber} />
                </List>

                <List>
                  <ListItem
                    title="One special character"
                    valid={containsSpecialCharacters}
                  />
                  <ListItem
                    title="8 character minimum"
                    valid={validMinimumLength}
                  />
                  <ListItem
                    title="50 characters maximum"
                    valid={validMaximumLength}
                  />
                </List>
              </div>
            </FormRow>

            <FormRow className={styles.submit}>
              {isResetRejected && <ErrorBlock error={resetError} />}

              <Button
                label="Reset Password"
                type="submit"
                variant="primary"
                size="wide"
                className={styles.submit}
                isDisabled={!isValid}
                isPending={isResetPending}
              />
            </FormRow>
          </Form>
        </div>
      </div>
      <div className={styles.footer}>
        <div>Copyright &copy; Tickmark, Inc {new Date().getFullYear()}</div>
      </div>
    </div>
  )
}

interface ListItemProps {
  title: string
  valid: boolean
}

function ListItem({ title, valid }: ListItemProps) {
  return (
    <li>
      <Icon
        className={styles.icon}
        size={16}
        name={valid ? 'checkbox-invert' : 'close'}
      />{' '}
      {title}
    </li>
  )
}

interface ListProps extends HTMLAttributes<HTMLUListElement> {}

function List({ children }: ListProps) {
  return <ul className={styles.list}>{children}</ul>
}
